import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { toast } from "sonner";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Reset password | RoleBoard" },
      {
        name: "description",
        content: "Choose a new password for your RoleBoard account.",
      },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://roleboard.app/reset-password" }],
  }),
  component: ResetPasswordPage,
});

const schema = z.object({
  password: z.string().min(6, "At least 6 characters").max(72),
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Supabase recovery link sets a session via the URL hash.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
        setReady(true);
      }
    });
    // Also check existing session in case the event already fired.
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse({ password });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: parsed.data.password,
      });
      if (error) throw error;
      toast.success("Password updated");
      navigate({ to: "/auditions", search: {} as never });
    } catch (err: any) {
      toast.error(err?.message ?? "Could not reset password");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative min-h-screen bg-background text-foreground overflow-hidden">
      <div className="min-h-screen grid place-items-center px-5 py-10">
        <div className="w-full max-w-md">
          <Link to="/" className="flex flex-col items-center gap-3 mb-10" aria-label="RoleBoard home">
            <BrandLogo className="h-20 md:h-24 w-auto" />
          </Link>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-semibold tracking-tight leading-tight">
              Set a new password
            </h1>
          </div>

          <div className="rounded-2xl border border-border bg-card/60 backdrop-blur-xl p-6 shadow-xl shadow-black/20">
            {!ready ? (
              <div className="text-center space-y-3">
                <Loader2 className="size-5 animate-spin text-muted-foreground mx-auto" />
                <p className="text-sm text-muted-foreground">
                  Validating reset link…
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="password">New password</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    placeholder="At least 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="w-full h-10" disabled={submitting}>
                  {submitting ? <Loader2 className="size-4 animate-spin" /> : "Update password"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
